<template>
  <div v-rect-loading="loading" class="open-api-management-container">
    <TableList
      class="open-api-list"
      row-key="id"
      :titleI18nKey="`${i18nPrefix}.title.list`"
      :list-data="openApiList"
      :table-columns="tableColumns"
      :show-create-btn="false"
      @action="handleAction"
    >
      <template #path="{formattedData}">
        <span class="path">{{ formattedData }}</span>
        <div
          v-clipboard:copy="formattedData"
          v-clipboard:success="handleCopySuccess"
          v-clipboard:error="handleCopyError"
          class="copy-btn"
        >
          <i class="btn-icon el-icon-document-copy"></i>
        </div>
      </template>
      <template #btn>
        <div v-rect-loading="exportStatus.loading" class="export-btn" @click="onExportBtnClick">
          <i class="btn-icon el-icon-printer"></i>
          <span class="btn-label">{{ $t('common.btn.export') }}</span>
        </div>
      </template>
    </TableList>
    <TableList
      v-if="customApiId"
      v-rect-loading="configListLoading"
      class="custom-path-list"
      row-key="id"
      :titleI18nKey="`${i18nPrefix}.title.configRequest`"
      :listData="configList"
      :tableColumns="configTableColumns"
      @create="handleConfigCreate"
      @action="handleConfigAction"
    >
      <template #customPath="{formattedData}">
        <span class="custom-path">{{ formattedData }}</span>
        <div
          v-clipboard:copy="formattedData"
          v-clipboard:success="handleCopySuccess"
          v-clipboard:error="handleCopyError"
          class="copy-btn"
        >
          <i class="btn-icon el-icon-document-copy"></i>
        </div>
      </template>
      <template #requireAuth="{ data, row }">
        <el-switch :value="data" @change="onRequireAuthChange(row)"></el-switch>
      </template>
      <template #isActive="{ data, row }">
        <el-switch :value="data" @change="onIsActiveChange(row)"></el-switch>
      </template>
    </TableList>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { AsyncTask } from '@/utils/common'
import { CUSTOM_API_TYPE, OPEN_API_BASE_PATH } from '@/constant/openApi'

export default {
  components: {
    TableList
  },
  data () {
    return {
      i18nPrefix: 'admin.openApi',
      loading: false,
      configListLoading: false,
      openApiList: [],
      configList: [],
      customPathPrefix: null,
      exportStatus: {
        loading: false,
        params: {},
        task: null
      }
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          labelI18nKey: `${this.i18nPrefix}.label.type`,
          dataKey: 'type',
          formatter: (data) => this.$t(`${this.i18nPrefix}.openApiType.${data}`)
        },
        {
          slotName: 'path',
          labelI18nKey: `${this.i18nPrefix}.label.path`,
          dataKey: 'path',
          cellClass: 'path-container',
          formatter: (data) => `${window.location.origin}${OPEN_API_BASE_PATH}${data}`
        }
      ]
      return columns
    },
    configTableColumns () {
      const columns = [
        {
          width: '100px',
          labelI18nKey: `${this.i18nPrefix}.label.type`,
          dataKey: 'open_api.type',
          formatter: (data) => this.$t(`${this.i18nPrefix}.openApiType.${data}`)
        },
        {
          width: '100px',
          labelI18nKey: `${this.i18nPrefix}.label.name`,
          dataKey: 'custom_name'
        },
        {
          slotName: 'customPath',
          width: '370px',
          labelI18nKey: `${this.i18nPrefix}.label.path`,
          dataKey: 'custom_path',
          cellClass: 'custom-path-container',
          formatter: (data) => `${window.location.origin}${OPEN_API_BASE_PATH}${this.customPathPrefix || '/'}${data}/`
        },
        {
          slotName: 'requireAuth',
          labelI18nKey: `${this.i18nPrefix}.label.isRequireAuth`,
          dataKey: 'require_auth'
        },
        {
          slotName: 'isActive',
          labelI18nKey: `${this.i18nPrefix}.label.isActive`,
          dataKey: 'is_active',
          formatter: (data) => this.$t(`common.label.${data ? 'yes' : 'no'}`)
        }
      ]
      return columns
    },
    customApiId () {
      const api = _.find(this.openApiList, { type: CUSTOM_API_TYPE })
      return api?.id
    }
  },
  created () {
    this.refreshData()
  },
  methods: {
    ...mapActions('openApi', ['getOpenApiList', 'createExportOpenApiMetaDataTask', 'getExportOpenApiMetaDataTask', 'getOpenApiRequestConfigList', 'getOpenApiConfigList', 'updateOpenApiConfigStatus']),
    async refreshData () {
      this.loading = true
      try {
        const resp = await this.getOpenApiList()
        if (resp) {
          this.openApiList = resp
          await this.loadConfigList()
        }
      } finally {
        this.loading = false
      }
    },
    async loadConfigList () {
      const resp = await this.getOpenApiConfigList({
        open_api_id: this.customApiId,
        has_custom_path: 1
      })
      if (resp) {
        this.customPathPrefix = resp.custom_path_prefix
        this.configList = resp.config_list
      }
    },
    handleAction (rowData, btn, btnName, index) {
      this.$router.push(`/v-admin/company-admin/open-api/${rowData.id}/config/response`)
    },
    handleConfigCreate () {
      this.$router.push(`/v-admin/company-admin/open-api/${this.customApiId}/config/request/add`)
    },
    handleConfigAction (rowData, btn, btnName, index) {
      this.$router.push(`/v-admin/company-admin/open-api/${rowData.open_api.id}/config/request/${rowData.id}/edit`)
    },
    onExportBtnClick () {
      const exportStatus = this.exportStatus
      if (exportStatus.loading) {
        return
      }
      exportStatus.loading = true
      exportStatus.task = new AsyncTask(() => this.createExportOpenApiMetaDataTask(exportStatus.params), (taskId) => this.getExportOpenApiMetaDataTask({
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/open-api-management/meta-info/export/?task_id=${taskId}&download=1`)
        exportStatus.loading = false
        exportStatus.task = null
        exportStatus.params = {}
      })
    },
    onRequireAuthChange (rowData) {
      this.saveConfigUpdateInfo(rowData, { require_auth: !rowData.require_auth })
    },
    onIsActiveChange (rowData) {
      this.saveConfigUpdateInfo(rowData, { is_active: !rowData.is_active })
    },
    async saveConfigUpdateInfo (configInfo, updateInfo) {
      this.configListLoading = true
      try {
        const openApiId = configInfo.open_api.id
        const configId = configInfo.id
        await this.updateOpenApiConfigStatus({ openApiId, configId, updateInfo })
        _.merge(configInfo, updateInfo)
      } finally {
        this.configListLoading = false
      }
    },
    handleCopySuccess () {
      this.$message.success(this.$t('common.message.actionSuccess', { action: this.$t('common.btn.copy') }))
    },
    handleCopyError () {
      this.$message.error(this.$t('common.message.actionFail', { action: this.$t('common.btn.copy') }))
    }
  }
}
</script>

<style lang="scss" scoped>
.open-api-management-container {
  .open-api-list {
    .export-btn {
      line-height: 22px;
      color: #0096cc;
      cursor: pointer;

      .btn-icon {
        font-size: 20px;
        vertical-align: middle;
      }

      .btn-label {
        margin-left: 12px;
        font-size: 16px;
        vertical-align: middle;
      }
    }

    ::v-deep {
      .path-container {
        .cell {
          display: flex;
        }
      }
    }

    .path {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .custom-path-list {
    margin-top: 40px;

    ::v-deep {
      .custom-path-container {
        .cell {
          display: flex;
        }
      }
    }

    .custom-path {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .copy-btn {
    margin-left: 10px;
    cursor: pointer;
    color: #0096cc;
    font-size: 16px;
  }
}
</style>
